/* eslint-disable use-isnan */
import { Flex, Text } from '@chakra-ui/layout';
import { parse, isAfter } from 'date-fns';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import {
  getBudgetControlHoursProgress,
  getBudgetControlHoursResume,
  IGetBudgetControlHoursResume,
} from 'services/budgetControl';
import { useBoolean } from 'hooks/useBoolean';

import { checkPermission } from 'utils/authentication';
import { formatCurrency } from 'utils/number';
import { queryToObj } from 'utils/query';
import { getLoggedUser } from 'utils/storage';
import { EmptyLayout, HoursBar } from './components';
import { Loading, Button } from 'components';
import { useDisclosure } from '@chakra-ui/react';

import * as Styles from './styles';
import { openNotification } from 'utils/notification';
import { getDetailedTimeTracking } from 'services/detailedTimeTracking';
import { ModalHoursHistoric } from '../Header/components/ModalHoursHistoric';

interface IProps {
  history: RouteComponentProps['history'];
}

interface IFormData {
  periodoInicial: string;
  periodoFinal: string;
}

const HoursPackComponent: React.FC<IProps> = ({ history }) => {
  const [isLoading, toggleIsLoading] = useBoolean(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [clientSelected, setClientSelected] = useState(false);
  const [resume, setResume] = useState<IGetBudgetControlHoursResume>();
  const [progress, setProgress] = useState<any>();

  const handleSearch = useCallback(async (query?: string) => {
    const queryObj = queryToObj(query || '');
    toggleIsLoading(true);

    if (queryObj) {
      setClientSelected(!!queryObj.idCliente);
    }

    try {
      if (!!getLoggedUser().client !== null) {
        const progressResponse = await getBudgetControlHoursProgress(query || '');
        const resumeResponse = await getBudgetControlHoursResume(query || '');
        setResume(resumeResponse.data);
        setProgress(progressResponse.data);
        toggleIsLoading(false);
      }
    } catch (error) {
      console.log('error');
    }
  }, []);

  useEffect(() => {
    handleSearch();

    const removeListen = history.listen(location => handleSearch(location.search));

    return () => {
      removeListen();
    };
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSubmit = useCallback(async (data: IFormData): Promise<void> => {
    const monthInitialPeriod = Number(data.periodoInicial.slice(0, 2));
    const monthLastPeriod = Number(data.periodoFinal.slice(0, 2));

    const arrDataPeriodoInitial = data.periodoInicial.split('/');
    const arrDataPeriodoFinal = data.periodoFinal.split('/');

    const [monthInitial, dayInitial, yearInitial] = [
      arrDataPeriodoInitial[0],
      '01',
      ...arrDataPeriodoInitial.slice(1),
    ];
    const [monthFinal, dayFinal, yearFinal] = [
      arrDataPeriodoFinal[0],
      '01',
      ...arrDataPeriodoFinal.slice(1),
    ];

    const periodoInitial = new Date(`${monthInitial}/${dayInitial}/${yearInitial}`);
    const periodoFinal = new Date(`${monthFinal}/${dayFinal}/${yearFinal}`);

    const isInitialPeriodGreaterThanTheCurrentDate = periodoInitial > new Date();
    if (
      data.periodoInicial.slice(0, 2) === '00' ||
      data.periodoFinal.slice(0, 2) === '00' ||
      data.periodoInicial.slice(3) === '0000 ' ||
      data.periodoFinal.slice(3) === '0000'
    ) {
      openNotification('Período inicial ou final inválido.', 'error');
    } else if (periodoFinal < periodoInitial) {
      openNotification('O período final não pode ser inferior ao período inicial.', 'error');
      return;
    } else if (isInitialPeriodGreaterThanTheCurrentDate) {
      openNotification('O período inicial não deve ser maior que a data atual.', 'error');
      return;
    } else if (monthInitialPeriod > 12) {
      openNotification('Mês do período inicial está inválido!', 'error');
      return;
    } else if (monthLastPeriod > 12) {
      openNotification('Mês do período final está inválido!', 'error');
      return;
    }

    const { data: res, headers } = await getDetailedTimeTracking({
      periodoInicial: data.periodoInicial,
      periodoFinal: data.periodoFinal,
    });
  }, []);

  const isExpired = useMemo(() => {
    if (!resume?.expiracao && !!getLoggedUser()?.enableHoursBank) return true;
    else if (!resume?.expiracao) return false;
    const exp = parse(resume.expiracao, 'dd/MM/yyyy', Date.now());
    return isAfter(Date.now(), exp);
  }, [resume]);

  return (
    <Styles.Container>
      {clientSelected ||
      (checkPermission({ onlyClient: true }) &&
        !!getLoggedUser()?.enableHoursBank &&
        !isExpired) ? (
        <>
          <legend>Pacote de horas vigente</legend>

          {isLoading ? (
            <Loading />
          ) : (
            <>
              <HoursBar
                progress={progress}
                isDebit={!!progress?.saldo.includes('-')}
                inDebt={progress?.saldo.includes('-')}
              />
              <Flex justify={'space-between'} align="center">
                <Flex
                  justify="center"
                  fontSize="0.8rem"
                  mt="auto"
                  direction={'column'}
                  gridGap="1rem"
                  height="100%"
                  paddingTop={'1rem'}
                >
                  <Flex alignItems="center">
                    <Text
                      as="p"
                      fontSize={'14px !important'}
                      fontWeight="700"
                      color="#000"
                      padding={'0 !important'}
                    >
                      Contratada: {progress?.contratada}
                    </Text>
                  </Flex>
                  <Flex alignItems="center">
                    <Styles.Dot />
                    <Text
                      as="p"
                      fontSize={'14px !important'}
                      fontWeight="700"
                      color="#000"
                      padding={'0 !important'}
                    >
                      Saldo: &nbsp; {progress?.saldo}
                    </Text>
                  </Flex>

                  <Flex alignItems="center">
                    <Styles.Dot spentHours />
                    <Text
                      as="p"
                      fontSize={'14px !important'}
                      fontWeight="700"
                      color="#000"
                      padding={'0 !important'}
                    >
                      Gasto: &nbsp; {progress?.consumida}
                    </Text>
                  </Flex>
                </Flex>
                <Flex direction={'column'}>
                  <Button outline onClick={onOpen}>
                    Relatório de horas
                  </Button>
                  <Styles.Section>
                    Contratado:&nbsp; {formatCurrency(resume?.contratado || 0)}
                  </Styles.Section>

                  <Styles.Section>Expira em:&nbsp; {resume?.expiracao || '-'}</Styles.Section>
                </Flex>
              </Flex>

              <ModalHoursHistoric history={history} isOpen={isOpen} onClose={onClose} />
            </>
          )}
        </>
      ) : checkPermission({ onlyAdmin: true }) ? (
        <>
          <legend>Pacote de horas vigente</legend>

          <p>Selecione um cliente para visualizar</p>
        </>
      ) : (
        <EmptyLayout isZero={isExpired} />
      )}
    </Styles.Container>
  );
};

export const HoursPack = memo(HoursPackComponent);
